import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ViewContainerRef,
    ViewEncapsulation,
} from '@angular/core';
import { BaseAxesChart } from '@components/ui-chart/base-axes-chart';
import { ChartTooltipTrigger } from '@components/ui-chart/base-chart.types';
import {
    IChartCallbackParams,
    ILineChartClickEvent,
} from '@models';

import cloneDeep from 'lodash-es/cloneDeep';
import defaultsDeep from 'lodash-es/defaultsDeep';
import max from 'lodash-es/max';
import { takeUntil } from 'rxjs/operators';

import {
    BASE_MARK_POINT_OPTIONS,
    BASE_SERIES_OPTIONS,
    DEFAULT_AREA_OPACITY,
    LINE_CHART_OPTIONS,
    mapLineChartColorOptions,
    Y_AXIS_SPLIT_LINES,
} from './ui-line-chart.constants';
import {
    ILineChartData,
    LineChartData,
    MultilineChartData,
} from './ui-line-chart.types';

function isLineChartData(data: LineChartData | MultilineChartData): data is LineChartData {
    return (
        (data as ILineChartData).key &&
        data.values instanceof Array
    ) ||
        (
            data instanceof Array &&
            (
                !data.length ||
                typeof data[0] === 'number'
            )
        );
}

@Component({
    template: '',
    selector: 'ui-line-chart',
    styleUrls: ['../base-chart.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class UiLineChartComponent
    extends BaseAxesChart<ILineChartClickEvent>
    implements OnChanges, OnDestroy {

    @Input()
    public set data(value: LineChartData | MultilineChartData) {
        if (value == null) {
            this._data = [];
            return;
        }

        if (isLineChartData(value)) {
            this._data = [value];
        } else {
            this._data = value;
        }
    }

    @Input()
    public markMaxPointPerDataset?: boolean;

    @Input()
    public fillAreasBelowLines = false;

    @Input()
    public stack = false;

    @Input()
    public showYAxisProjectionLine = false;

    @Input()
    public smooth = false;

    @Input()
    public tooltipTrigger = ChartTooltipTrigger.Item;

    /**
     * The params array contains an object for each data series relevant to the tooltip (i.e. when it's triggered by axis, params will
     * contain an IChartCallbackParams for each data series; when it's triggered by item, params array will contain
     * a single IChartCallbackParams).
     */
    @Input()
    public tooltipFormatter?: (params: IChartCallbackParams[]) => string;

    private _data?: MultilineChartData;

    constructor(
        injector: Injector,
        protected _viewContainerRef: ViewContainerRef,
    ) {
        super(injector);
        this._extendOptions(LINE_CHART_OPTIONS);
        this._colorProvider.changes$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(() => {
                this._extendOptions(mapLineChartColorOptions(this._colorProvider));
            });
        this.chartOptions.tooltip.formatter = this._getTooltipText;
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);

        if (
            this.tooltipTrigger != null &&
            changes.tooltipTrigger
        ) {
            this.chartOptions.tooltip.trigger = this.tooltipTrigger;
            this.chartOptions.xAxis.axisPointer.show =
                this.tooltipTrigger === ChartTooltipTrigger.Axis;
        }

        if (
            this.showYAxisProjectionLine != null &&
            changes.showYAxisProjectionLine
        ) {
            this.chartOptions.tooltip.axisPointer.type =
                this.showYAxisProjectionLine ? 'cross' : 'line';
        }

        if (
            !this._data ||
            (
                !changes.data &&
                !changes.legend &&
                !changes.xAxisLabels &&
                !changes.colors &&
                !changes.fillAreasBelowLines &&
                !changes.markMaxPointPerDataset &&
                !changes.stack &&
                !changes.smooth
            )
        ) { return; }

        this.chartOptions.series = [];
        this.chartOptions.legend.data = [];
        this.chartOptions.xAxis.data = this.xAxisLabels || [];
        this.chartOptions.yAxis.splitNumber = 1;

        this._data.forEach(this._mapData);
    }

    public onSegmentClick(params: IChartCallbackParams) {
        if (
            this._data!.length &&
            this._data![0] instanceof Array
        ) {
            // Only emit click event when input data is of type ILineChartData (i.e. has a key for each data set).
            return;
        }

        const clickedDataSet: ILineChartData = this._data![params.seriesIndex] as ILineChartData;

        this.segmentClick.emit({
            key: clickedDataSet.key,
            value: params.value,
            xAxisLabel: params.name,
        } as ILineChartClickEvent);
    }

    /**
     * This method ensures that tooltip params look the same when you hover either a chart item (pie, bar, etc.) or a markPoint.
     * The tooltip shown when you hover over a markPoint doesn't have a seriesName.
     */
    private _normalizeTooltipParam = (params: IChartCallbackParams) => {
        if (
            params.seriesName == null &&
            params.seriesIndex != null &&
            this.legend &&
            this.legend[params.seriesIndex] != null
        ) {
            params.seriesName = this.legend[params.seriesIndex];
        }

        return params;
    }

    private _getTooltipText = (params: IChartCallbackParams | IChartCallbackParams[]) => {
        if (!(params instanceof Array)) {
            params = [params];
        }

        params = params.map(this._normalizeTooltipParam);

        if (this.tooltipFormatter) {
            return this.tooltipFormatter(params);
        }

        if (this.tooltipTrigger === ChartTooltipTrigger.Item) {
            return this._getSimpleTooltipText(params[0]);
        }

        return this._getComplexTooltipText(params);
    }

    private _getSimpleTooltipText = ({
        marker,
        name,
        seriesName,
        value,
    }: IChartCallbackParams) => {
        return `
            <div class="chart-tooltip-container">
                <div class="tooltip-title">
                    <span class="tooltip-marker">${marker}</span>
                    <strong>${value}</strong>
                    ${seriesName}
                </div>
                <div class="tooltip-subtitle">
                    ${name}
                </div>
            </div>
        `;
    }

    private _getComplexTooltipText = (params: IChartCallbackParams[]) => {
        return `
            <div class="chart-tooltip-container">
                <div class="tooltip-title">
                    ${params[0].name}
                </div>
                <table class="series-table">
                    ${params
                .map((param) => `
                            <tr>
                                <td><span class="tooltip-marker">${param.marker}</span></td>
                                <td class="small-data-value-cell">${param.value}</td>
                                <td class="series-name-cell">${param.seriesName}</td>
                            </tr>
                        `)
                .join('')}
                </table>
            </div>
        `;
    }

    private _mapData = (rawData: { values: any; }, index: number) => {
        const data = cloneDeep(
            rawData instanceof Array ?
                rawData :
                rawData.values,
        );
        const maxValue = max(data) as number;

        this.chartOptions.yAxis.splitNumber = Math.max(
            maxValue <= Y_AXIS_SPLIT_LINES ?
                Math.ceil(maxValue) :
                Y_AXIS_SPLIT_LINES,
            this.chartOptions.yAxis.splitNumber,
        );

        const series = defaultsDeep(
            {
                name: (this.legend || [])[index] || '',
                data,
                areaStyle: {
                    opacity: this.fillAreasBelowLines ? DEFAULT_AREA_OPACITY : 0,
                },
            },
            BASE_SERIES_OPTIONS,
        );

        if (this.legend) {
            this.chartOptions.legend.data.push({
                name: this.legend[index] || '',
                icon: 'circle',
            });
        }

        if (this.colors) {
            series.itemStyle.color = this.colors[index] || null;
            series.lineStyle.color = this.colors[index] || null;
        }

        if (
            this.markMaxPointPerDataset &&
            maxValue > 0
        ) {
            series.markPoint = cloneDeep(BASE_MARK_POINT_OPTIONS);
        }

        if (this.stack) {
            series.stack = 'stack';
        }

        series.smooth = this.smooth;

        this.chartOptions.series!.push(series);
    }
}
